import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload, FileSpreadsheet, ImageIcon, Download, CheckCircle2, XCircle, AlertTriangle,
  Loader2, Users, Camera, ShieldCheck, ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BulkCandidateImport({ examId, examName, onClose }) {
  const [activeTab, setActiveTab] = useState('import');
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [photoResult, setPhotoResult] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [error, setError] = useState('');
  const [expandedErrors, setExpandedErrors] = useState(false);
  const [uploadingPhotoFor, setUploadingPhotoFor] = useState(null);
  const photoInputRefs = useRef({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCandidates = useCallback(async () => {
    setLoadingCandidates(true);
    try {
      const res = await axios.get(`${API}/api/organiser/exams/${examId}/candidates`, { headers });
      setCandidates(res.data);
    } catch (err) { /* empty is fine */ }
    finally { setLoadingCandidates(false); }
  }, [examId]);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/api/organiser/exams/${examId}/download-template`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `candidate_template_${examName.replace(/\s+/g, '_')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { setError('Failed to download template'); }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/)) { setError('Please upload an Excel file (.xlsx or .xls)'); return; }
    setUploading(true); setError(''); setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/api/organiser/exams/${examId}/import-candidates`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      fetchCandidates();
    } catch (err) { setError(err.response?.data?.detail || 'Import failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleBulkPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isZip = file.name.endsWith('.zip');
    const isPhoto = /\.(jpg|jpeg|png|webp)$/i.test(file.name);
    if (!isZip && !isPhoto) { setError('Upload a ZIP file of photos or a single photo (JPG/PNG/WEBP)'); return; }
    setUploading(true); setError(''); setPhotoResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/api/organiser/exams/${examId}/upload-photos`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setPhotoResult(res.data);
      fetchCandidates();
    } catch (err) { setError(err.response?.data?.detail || 'Photo upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleIndividualPhotoUpload = async (candidateId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(jpg|jpeg|png|webp)$/i.test(file.name)) { setError('Only JPG, PNG, or WEBP photos allowed'); return; }
    setUploadingPhotoFor(candidateId); setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/api/organiser/exams/${examId}/candidates/${candidateId}/upload-photo`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      fetchCandidates();
    } catch (err) { setError(err.response?.data?.detail || 'Photo upload failed'); }
    finally { setUploadingPhotoFor(null); e.target.value = ''; }
  };

  const handleManualVerify = async (candidateId) => {
    try {
      await axios.post(`${API}/api/organiser/exams/${examId}/manual-verify/${candidateId}`, {}, { headers });
      fetchCandidates();
    } catch (err) { setError(err.response?.data?.detail || 'Manual verification failed'); }
  };

  const statusBadge = (status) => {
    const styles = {
      verified: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      manually_verified: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      blocked: 'bg-red-500/10 text-red-400 border-red-500/20',
      failed: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      pending: 'bg-zinc-700/50 text-zinc-400 border-zinc-600/30',
    };
    return styles[status] || styles.pending;
  };

  const tabs = [
    { id: 'import', label: 'Import', fullLabel: 'Import Candidates', icon: FileSpreadsheet },
    { id: 'photos', label: 'Photos', fullLabel: 'Upload Photos', icon: ImageIcon },
    { id: 'status', label: `Status (${candidates.length})`, fullLabel: `Verification (${candidates.length})`, icon: ShieldCheck },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="bulk-import-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] sm:mx-4 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-white truncate">Candidate Management</h2>
            <p className="text-zinc-500 text-xs sm:text-sm truncate">{examName}</p>
          </div>
          <button onClick={onClose} className="ml-2 text-zinc-500 hover:text-white transition-colors text-2xl leading-none flex-shrink-0 w-8 h-8 flex items-center justify-center" data-testid="close-import-modal">&times;</button>
        </div>

        {/* Tabs - scrollable on mobile */}
        <div className="flex border-b border-zinc-800 overflow-x-auto flex-shrink-0 scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex-shrink-0 flex-1 min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium flex items-center justify-center gap-1.5 sm:gap-2 transition-colors border-b-2 whitespace-nowrap ${
                activeTab === tab.id ? 'border-amber-400 text-amber-400 bg-amber-400/5' : 'border-transparent text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="sm:hidden">{tab.label}</span>
              <span className="hidden sm:inline">{tab.fullLabel}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-xs sm:text-sm flex-1">{error}</p>
              <button onClick={() => setError('')} className="text-red-400 hover:text-red-300 flex-shrink-0">&times;</button>
            </div>
          )}

          {/* Tab: Import */}
          {activeTab === 'import' && (
            <div data-testid="import-tab-content" className="space-y-4 sm:space-y-6">
              {/* Download template */}
              <div className="bg-zinc-800/40 border border-zinc-700/50 rounded-xl p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Download className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-medium text-sm sm:text-base mb-1">Step 1: Download Template</h4>
                    <p className="text-zinc-500 text-xs sm:text-sm mb-3">
                      Download the Excel template, fill in candidate details (Application Number, Full Name, Email, Mobile), then upload below.
                    </p>
                    <button
                      onClick={downloadTemplate}
                      data-testid="download-template-btn"
                      className="px-3 sm:px-4 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/20 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-2"
                    >
                      <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Download Template (.xlsx)
                    </button>
                  </div>
                </div>
              </div>

              {/* Upload area */}
              <div className="bg-zinc-800/40 border border-zinc-700/50 rounded-xl p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-medium text-sm sm:text-base mb-1">Step 2: Upload Filled Excel</h4>
                    <p className="text-zinc-500 text-xs sm:text-sm mb-3">
                      Candidates will be automatically assigned to this exam.
                    </p>
                    <label className="block" data-testid="excel-upload-area">
                      <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} className="hidden" disabled={uploading} data-testid="excel-file-input" />
                      <div className="border-2 border-dashed border-zinc-700 hover:border-amber-500/50 rounded-xl p-4 sm:p-6 text-center cursor-pointer transition-colors active:border-amber-500/70">
                        {uploading ? (
                          <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-amber-400 mx-auto" />
                        ) : (
                          <>
                            <FileSpreadsheet className="w-6 h-6 sm:w-8 sm:h-8 text-zinc-500 mx-auto mb-2" />
                            <p className="text-zinc-400 text-xs sm:text-sm">Tap to upload or drag & drop</p>
                            <p className="text-zinc-600 text-[10px] sm:text-xs mt-1">.xlsx or .xls (max 10MB)</p>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Import Result */}
              {importResult && (
                <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-3 sm:p-4" data-testid="import-result">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3">
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-white text-sm sm:text-base font-medium">{importResult.message}</span>
                  </div>
                  {importResult.imported?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-zinc-500 text-[10px] sm:text-xs font-medium mb-2">IMPORTED ({importResult.imported.length})</p>
                      <div className="space-y-1 max-h-28 sm:max-h-32 overflow-y-auto">
                        {importResult.imported.map((c, i) => (
                          <div key={i} className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm flex-wrap">
                            <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                            <span className="text-zinc-300 font-medium">{c.application_number}</span>
                            <span className="text-zinc-500 hidden sm:inline">—</span>
                            <span className="text-zinc-400">{c.name}</span>
                            <span className="text-zinc-600 text-[10px] sm:text-xs hidden sm:inline">({c.email})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {importResult.errors?.length > 0 && (
                    <div>
                      <button onClick={() => setExpandedErrors(!expandedErrors)} className="flex items-center gap-2 text-amber-400 text-xs font-medium">
                        <AlertTriangle className="w-3 h-3" />
                        {importResult.errors.length} Error(s)
                        {expandedErrors ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                      {expandedErrors && (
                        <div className="mt-2 space-y-1 max-h-28 overflow-y-auto">
                          {importResult.errors.map((e, i) => (
                            <div key={i} className="text-[10px] sm:text-xs text-red-400">Row {e.row}: {e.error}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tab: Photos */}
          {activeTab === 'photos' && (
            <div data-testid="photos-tab-content">
              <div className="bg-zinc-800/40 border border-zinc-700/50 rounded-xl p-4 sm:p-5 mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-violet-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-violet-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-medium text-sm sm:text-base mb-1">Bulk Upload — ZIP or Single Photo</h4>
                    <div className="bg-zinc-900/50 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4 text-[10px] sm:text-xs text-zinc-500 space-y-1">
                      <p>Name photos as: <span className="text-amber-400 font-mono">ApplicationNumber.jpg</span></p>
                      <p>Formats: JPG, PNG, WEBP &middot; Max: 5MB/photo, 100MB for ZIP</p>
                    </div>
                    <label className="block" data-testid="photo-upload-area">
                      <input type="file" accept=".zip,.jpg,.jpeg,.png,.webp" onChange={handleBulkPhotoUpload} className="hidden" disabled={uploading} data-testid="photo-file-input" />
                      <div className="border-2 border-dashed border-zinc-700 hover:border-violet-500/50 rounded-xl p-4 sm:p-6 text-center cursor-pointer transition-colors active:border-violet-500/70">
                        {uploading ? (
                          <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-violet-400 mx-auto" />
                        ) : (
                          <>
                            <ImageIcon className="w-6 h-6 sm:w-8 sm:h-8 text-zinc-500 mx-auto mb-2" />
                            <p className="text-zinc-400 text-xs sm:text-sm">Tap to upload ZIP or single photo</p>
                            <p className="text-zinc-600 text-[10px] sm:text-xs mt-1">.zip, .jpg, .png, .webp</p>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {photoResult && (
                <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-3 sm:p-4" data-testid="photo-result">
                  <p className="text-white text-sm sm:text-base font-medium mb-3">{photoResult.message}</p>
                  {photoResult.matched?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-emerald-400 text-[10px] sm:text-xs font-medium mb-1">MATCHED ({photoResult.matched.length})</p>
                      {photoResult.matched.map((m, i) => (
                        <div key={i} className="text-xs sm:text-sm text-zinc-400 flex items-center gap-2">
                          <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />{m.application_number} — {m.name}
                        </div>
                      ))}
                    </div>
                  )}
                  {photoResult.unmatched?.length > 0 && (
                    <div>
                      <p className="text-amber-400 text-[10px] sm:text-xs font-medium mb-1">UNMATCHED ({photoResult.unmatched.length})</p>
                      {photoResult.unmatched.map((u, i) => (
                        <div key={i} className="text-xs sm:text-sm text-zinc-500 flex items-center gap-2">
                          <AlertTriangle className="w-3 h-3 text-amber-400 flex-shrink-0" />{u.filename}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tab: Verification Status */}
          {activeTab === 'status' && (
            <div data-testid="status-tab-content">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h4 className="text-white text-sm sm:text-base font-medium">Candidate Verification</h4>
                <button onClick={fetchCandidates} className="text-zinc-500 hover:text-white transition-colors p-1" data-testid="refresh-candidates-btn">
                  <RefreshCw className={`w-4 h-4 ${loadingCandidates ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {candidates.length === 0 ? (
                <div className="text-center py-10 sm:py-12 text-zinc-500">
                  <Users className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-xs sm:text-sm">No candidates imported yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {candidates.map((c) => (
                    <div
                      key={c.id}
                      className="bg-zinc-800/40 border border-zinc-700/50 rounded-lg p-3 sm:p-3.5"
                      data-testid={`candidate-row-${c.application_number}`}
                    >
                      {/* Row: info + status */}
                      <div className="flex items-center gap-2.5 sm:gap-3">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-zinc-700 rounded-lg flex items-center justify-center flex-shrink-0">
                          {c.photo_uploaded ? (
                            <ImageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" />
                          ) : (
                            <ImageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-zinc-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                            <span className="text-white text-xs sm:text-sm font-medium truncate">{c.full_name}</span>
                            <span className="text-zinc-600 text-[10px] sm:text-xs font-mono">{c.application_number}</span>
                          </div>
                          <p className="text-zinc-500 text-[10px] sm:text-xs truncate">{c.email}</p>
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full border ${statusBadge(c.verification_status)}`}>
                            {c.verification_status === 'manually_verified' ? 'Manual OK' : c.verification_status}
                          </span>
                        </div>
                      </div>

                      {/* Action row: upload photo + manual verify */}
                      <div className="mt-2 pt-2 border-t border-zinc-800/60 flex items-center gap-2 flex-wrap">
                        {/* Individual photo upload */}
                        <label className="cursor-pointer flex-shrink-0" data-testid={`upload-photo-${c.application_number}`}>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp"
                            className="hidden"
                            ref={(el) => { photoInputRefs.current[c.id] = el; }}
                            onChange={(e) => handleIndividualPhotoUpload(c.id, e)}
                            disabled={uploadingPhotoFor === c.id}
                          />
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] sm:text-xs font-medium transition-colors ${
                            c.photo_uploaded
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20'
                              : 'bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:bg-violet-500/20'
                          }`}>
                            {uploadingPhotoFor === c.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Camera className="w-3 h-3" />
                            )}
                            {c.photo_uploaded ? 'Replace Photo' : 'Upload Photo'}
                          </span>
                        </label>

                        {/* Manual verify button */}
                        {(c.verification_status === 'blocked' || c.verification_status === 'failed' || c.verification_status === 'pending') && c.photo_uploaded && (
                          <button
                            onClick={() => handleManualVerify(c.id)}
                            data-testid={`manual-verify-${c.application_number}`}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 rounded-md text-[10px] sm:text-xs font-medium transition-colors flex-shrink-0"
                          >
                            <ShieldCheck className="w-3 h-3" />
                            Verify Manually
                          </button>
                        )}

                        {c.photo_uploaded && (
                          <span className="text-[10px] sm:text-xs text-emerald-500/70 ml-auto flex-shrink-0">Photo attached</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
